import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Plus, Minus } from "lucide-react";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Input } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/input";
import { Label } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/label";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { useToast } from "@/projects/Admin_Bite_Balance/bite-balance/src/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/select";

const servingTypes = [
  { value: "plate", label: "Plate" },
  { value: "bowl", label: "Bowl" },
  { value: "cup", label: "Cup" },
  { value: "glass", label: "Glass" },
  { value: "piece", label: "Piece" },
  { value: "grams", label: "Serving Size (grams)" },
  { value: "other", label: "Other" },
];

const servingSizes = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
];

const AddFoodItem = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const mealType = location.state?.mealType || "breakfast";

  const [foodName, setFoodName] = useState("");
  const [servingType, setServingType] = useState("");
  const [customServing, setCustomServing] = useState("");
  const [servingSize, setServingSize] = useState("");
  const [numberOfServings, setNumberOfServings] = useState(1);

  const handleIncrement = () => {
    setNumberOfServings(prev => prev + 1);
  };

  const handleDecrement = () => {
    setNumberOfServings(prev => (prev > 1 ? prev - 1 : 1));
  };

  const handleSave = () => {
    if (!foodName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a food name",
        variant: "destructive",
      });
      return;
    }

    if (!servingType) {
      toast({
        title: "Error",
        description: "Please select a serving type",
        variant: "destructive",
      });
      return;
    }

    if (
      (servingType === "other" || servingType === "grams") &&
      !customServing.trim()
    ) {
      toast({
        title: "Error",
        description:
          servingType === "grams"
            ? "Please enter the serving size in grams"
            : "Please enter the serving size",
        variant: "destructive",
      });
      return;
    }

    if (!servingSize) {
      toast({
        title: "Error",
        description: "Please select a serving size",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Food item added successfully",
    });
    navigate("/meals/prediction");
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b border-border bg-background">
        <div className="flex items-center justify-between p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/meals/prediction")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold">Add Food Item</h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="space-y-6 p-4">
        <Card className="space-y-6 border-2 p-6">
          {/* Food Name */}
          <div className="space-y-2">
            <Label htmlFor="foodName" className="font-bold">
              Food Name *
            </Label>
            <Input
              id="foodName"
              placeholder="e.g., Grilled Chicken Breast"
              value={foodName}
              onChange={e => setFoodName(e.target.value)}
              className="border-2"
            />
          </div>

          {/* Serving Type Dropdown */}
          <div className="space-y-2">
            <Label className="font-bold">Serving Type *</Label>
            <Select value={servingType} onValueChange={setServingType}>
              <SelectTrigger className="border-2">
                <SelectValue placeholder="Select serving type" />
              </SelectTrigger>
              <SelectContent className="border-2 bg-background">
                {servingTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Serving Input (shown when "other" or "grams" is selected) */}
          {(servingType === "other" || servingType === "grams") && (
            <div className="space-y-2">
              <Label htmlFor="customServing" className="font-bold">
                {servingType === "grams"
                  ? "Serving Size (grams) *"
                  : "Exact Serving Size (g or mL) *"}
              </Label>
              <Input
                id="customServing"
                placeholder={
                  servingType === "grams" ? "e.g., 150" : "e.g., 150g or 200mL"
                }
                value={customServing}
                onChange={e => setCustomServing(e.target.value)}
                className="border-2"
                type={servingType === "grams" ? "number" : "text"}
              />
            </div>
          )}

          {/* Serving Size Dropdown */}
          <div className="space-y-2">
            <Label className="font-bold">Portion Size *</Label>
            <Select value={servingSize} onValueChange={setServingSize}>
              <SelectTrigger className="border-2">
                <SelectValue placeholder="Select portion size" />
              </SelectTrigger>
              <SelectContent className="border-2 bg-background">
                {servingSizes.map(size => (
                  <SelectItem key={size.value} value={size.value}>
                    {size.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Indicate whether your {servingType || "serving"} is small, medium,
              or large
            </p>
          </div>

          {/* Number of Servings */}
          <div className="space-y-2">
            <Label className="font-bold">Number of Servings *</Label>
            <div className="flex items-center justify-center gap-6">
              <Button
                variant="outline"
                size="icon"
                onClick={handleDecrement}
                className="h-12 w-12 rounded-full border-2"
              >
                <Minus className="h-5 w-5" />
              </Button>
              <span className="min-w-[3rem] text-center text-2xl font-bold">
                {numberOfServings}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={handleIncrement}
                className="h-12 w-12 rounded-full border-2"
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-4">
            <Button className="w-full" size="lg" onClick={handleSave}>
              Save
            </Button>
            <Button
              variant="outline"
              className="w-full"
              size="lg"
              onClick={() => navigate("/meals/prediction")}
            >
              Cancel
            </Button>
          </div>
        </Card>

        <Card className="border-2 bg-muted p-4">
          <p className="text-sm text-muted-foreground">
            <span className="font-bold">Tip:</span> Select the type and size of
            your serving for more accurate nutritional calculations.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default AddFoodItem;
